'use client';

import Image from 'next/image';
import type { MouseEventHandler } from 'react';
import { useMemo, useState } from 'react';

import {
  ListTypeLabel,
// eslint-disable-next-line @stylistic/max-len
} from '@/app/(routes)/listing/_components/page-content/search-list/car-card/listing-type-label';
import { ImageWithFallback } from '@/components/image-with-fallback';
import { TITLE_HEADING_H2 } from '@/components/section-container/base';
import {
  TITLE_HEADING_DIV,
} from '@/components/section-container/base/constants';
import { SectionContainer } from '@/components/section-container/page';
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock';
import { joinSgcmMedia } from '@/utils/url/join/public';

import { Alert } from '../../../_components/page-content/alert';
import {
  PAST_CAR_TERMINATED_PROMPT,
} from '../../../_components/page-content/alert/constants';
import {
  SECTION_TITLE_RECOMMENDED_FOR_YOU,
} from '../../../_components/page-content/constants';
import {
  GalleryViewer,
} from '../../../_components/page-content/gallery-viewer';
import {
  RecommendedForYouForInfo,
} from '../../../_components/page-content/recommended-for-you-for-info';
import {
  SponsoredContent,
} from '../../../_components/page-content/sponsored-content';
import styles from './styles.module.scss';
import type { Component } from './types';

export const PageContent: Component = ({
  carDetailData,
  promotions,
}) => {
  const [isGalleryViewerOpen, setIsGalleryViewerOpen] = useState(false);
  const [galleryViewerSlide, setGalleryViewerSlide] = useState(0);
  const stockImages = carDetailData.imageNums
    .filter(image => image.subModelId === 0);

  const photos = useMemo(() => carDetailData.imageNums
    .map(image => ({
      name: `${carDetailData.make} ${carDetailData.model} ${carDetailData.subModels
        .find(submodel => submodel.id === image.subModelId
        && submodel.isAvailable)?.name ?? ''}`,
      url: `https://i.i-sgcm.com/new_cars/cars/${carDetailData.id}/${carDetailData.id}_g${image.imageNum}`,
    })), [
    carDetailData.id,
    carDetailData.imageNums,
    carDetailData.make,
    carDetailData.model,
    carDetailData.subModels,
  ]);

  const handlePhotoClick: MouseEventHandler<HTMLImageElement> = (e) => {
    const elements = document.getElementsByClassName(styles.imagePhoto);
    const index = Array.prototype.indexOf.call(elements, e.target);

    setIsGalleryViewerOpen(true);
    setGalleryViewerSlide(index);
  };

  const promo = useMemo(() => promotions
    .find(
      promotion => promotion.promoType === 'tactical_ads'
      && promotion.competitorCars?.find(
        id => id === carDetailData.id.toString(),
      ),
    ), [carDetailData.id, promotions]);

  useBodyScrollLock({ isLocked: isGalleryViewerOpen });

  const isPastCar = !(carDetailData.additionalProperties.isAvailable
    || carDetailData.additionalProperties.isUpcoming);

  return (
    <main className={styles.containerPageRoot}>
      <GalleryViewer
        images={photos}
        initialSideIndex={galleryViewerSlide}
        isOpen={isGalleryViewerOpen}
        onClose={() => setIsGalleryViewerOpen(false)} />
      {
        isPastCar
        && (
          <SectionContainer
            title=""
            titleHeading={TITLE_HEADING_DIV}>
            <Alert
              date={carDetailData.terminationDate}
              text={PAST_CAR_TERMINATED_PROMPT} />
          </SectionContainer>
        )
      }
      {promo && (
        <SectionContainer
          title=""
          titleHeading={TITLE_HEADING_DIV}>
          <SponsoredContent
            modelId={carDetailData.id}
            promotions={promotions} />
        </SectionContainer>
      )}
      {stockImages.length > 0 && (
        <SectionContainer
          title={`Stock Photos Of ${carDetailData.make} ${carDetailData.model}`}
          titleHeading={TITLE_HEADING_H2}>
          <div className={styles.containerPhotoList}>
            {stockImages.map(image => (
              <ImageWithFallback
                key={image.imageNum}
                alt={`${carDetailData.make} ${carDetailData.model} ${image.imageNum}`}
                className={styles.imagePhoto}
                fallback={image.imageNum === 0
                  ? carDetailData.image.thumbnailUrl
                  : `https://i.i-sgcm.com/new_cars/cars/${carDetailData.id}/${carDetailData.id}_g${image.imageNum}.jpg`}
                height={144}
                src={image.imageNum === 0
                  ? carDetailData.image.thumbnailUrl
                  : `https://i.i-sgcm.com/new_cars/cars/${carDetailData.id}/${carDetailData.id}_g${image.imageNum}_b.jpg`}
                width={216}
                onClick={handlePhotoClick} />
            ))}
          </div>
        </SectionContainer>
      )}
      {carDetailData.subModels
        .toSorted((a, b) => a.id - b.id)
        .map((subModel) => {
          const subModelImages = carDetailData.imageNums
            .filter(image => image.subModelId === subModel.id
            && subModel.isAvailable);

          return (subModelImages.length > 0 || subModel.has360) && (
            <SectionContainer
              key={subModel.id}
              title={`${carDetailData.make} ${carDetailData.model} ${subModel.name}`}
              titleAfterContent={subModel.isFacelift && (
                <ListTypeLabel
                  labelSize={10}
                  type="facelift" />
              )}
              titleHeading={TITLE_HEADING_H2}>
              <div className={styles.containerPhotoList}>
                {subModel.has360
                && (
                  <a
                    className={styles.container360Photo}
                    href={`https://i.i-sgcm.com/new_cars/pano360_viewer.php?specCode=${subModel.id}&carCode=${carDetailData.id}`}
                    rel="noreferrer"
                    target="_blank"
                    onClick={e => e.stopPropagation()}>
                    <ImageWithFallback
                      alt={`${carDetailData.make} ${subModel.name} 360 thumbnail`}
                      className={styles.image360Photo}
                      height={144}
                      src={`https://i.i-sgcm.com/new_cars/cars/${carDetailData.id}/pano/${subModel.id}_tn.jpg`}
                      width={216} />
                    <div className={styles.container360ViewBadge}>
                      <Image
                        alt="360 view"
                        height={36}
                        src={`${joinSgcmMedia('/public/new-cars/icon/iconHas360.svg')}`}
                        width={36} />
                      <span className={styles.textViewIn360}>View in 360°</span>
                    </div>
                  </a>
                )}
                {subModelImages.map((image, idx) => (
                  <ImageWithFallback
                    key={image.imageNum}
                    alt={`${carDetailData.make} ${carDetailData.model} ${subModel.name} ${image.imageNum}`}
                    className={styles.imagePhoto}
                    fallback={`https://i.i-sgcm.com/new_cars/cars/${carDetailData.id}/${carDetailData.id}_g${image.imageNum}.jpg`}
                    height={144}
                    priority={idx === 0}
                    src={`https://i.i-sgcm.com/new_cars/cars/${carDetailData.id}/${carDetailData.id}_g${image.imageNum}_b.jpg`}
                    width={216}
                    onClick={handlePhotoClick} />
                ))}
              </div>
            </SectionContainer>
          );
        })}
      <SectionContainer
        title={SECTION_TITLE_RECOMMENDED_FOR_YOU}
        titleHeading={TITLE_HEADING_H2}>
        <RecommendedForYouForInfo />
      </SectionContainer>
    </main>
  );
};
